<template>
  <div>
    <div class="crew gap" v-if="isPreGap">
      <div class="number">...</div>
      <div class="sidebar-content"></div>
    </div>
    <div
      class="crew crew-element"
      :class="{ folded: isCollectionFolded && !isCurrent }"
      :id="'element-' + index"
      ref="element"
      v-if="isDisplayed || isCardinal"
    >
      <div class="crew-next-page" v-if="showLoadPrev">
        <a href @click.prevent="loadPrev()" class="btn btn-round" v-translate>Éléments précédents</a>
      </div>
      <div class="crew-prev" v-show="isCurrent && !isFirst">
        <a href @click.prevent="goTo('previous')">
          <i class="icon-tri_up"></i>
        </a>
      </div>
      <div class="number" @click="setCurrent">{{ index + 1 }}</div>
      <div :class="['sidebar-content', { active: isActive }]">
        <div class="tools" v-if="element.related && isSeparable">
          <div class="tool-links">
            <FavoriteLink :artworkId="element._id" />
          </div>
          <div class="share-print">
            <a href class="expand" @click.prevent.stop>
              <i class="icon-forms"></i>
            </a>
            <a @click.prevent="share(element)" class="share" v-b-tooltip.hover.left :title="titles.share">
              <i class="icon-partager"></i>
            </a>
            <PrintLink link="artwork" :artwork="printArtwork" />
            <TextNoteLink
              type="artwork"
              :resource="artwork"
              :artworkEl="element"
              v-if="$can('create', 'text_note')"
              :title="titles.create"
              icon="Notice_New"
            ></TextNoteLink>
          </div>
        </div>

        <ArtworkElement
          v-if="!isCollectionFolded || isCurrent"
          :artwork="highlightedElement"
          :rawArtwork="element"
          @setCurrent="setCurrent"
          :index="index"
          on-is-open-note="el.isOpenNote(note)"
          on-toggle-note="el.toggleNote(note, options)"
          on-set-current-artwork="el.setCurrentArtwork(artwork)"
        />
      </div>
      <div class="crew-next" v-show="isCurrent && !isLast">
        <a href @click.prevent="goTo('next')">
          <i class="icon-tri_down"></i>
        </a>
      </div>
      <div class="crew-next-page" v-if="showLoadNext">
        <a href @click.prevent="loadNext()" class="btn btn-round" v-translate>Éléments suivants</a>
      </div>
    </div>
    <div class="crew gap" v-if="isPostGap">
      <div class="number">...</div>
      <div class="sidebar-content"></div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import ArtworkElement from './ArtworkElement.vue';
import TextNoteLink from './TextNoteLink.vue';
import FavoriteLink from './FavoriteLink.vue';
import PrintLink from './PrintLink.vue';
import applyHighlights from 'lib/applyHighlights.js';
import shareMixin from 'mixins/shareMixin.js';
import abilitiesMixin from 'mixins/abilitiesMixin.js';

import { elementsPerPage } from 'config';

export default {
  components: {
    ArtworkElement,
    TextNoteLink,
    FavoriteLink,
    PrintLink,
  },
  mixins: [abilitiesMixin, shareMixin],
  props: ['element', 'index', 'count', 'is-folded', 'current', 'start', 'end'],
  mounted() {
    this.$watch(
      '$route',
      function (to, from) {
        const { index } = to.query;
        const parsedIndex = parseInt(index);

        if (!isNaN(parsedIndex) && typeof parsedIndex === 'number' && parsedIndex === this.index) {
          this.assign({ name: 'images', data: this.element.imageSet.images });
          window.scrollTo(0, this.$refs.element.offsetTop);
        }
      },
      { immidiate: true }
    );
  },
  computed: {
    ...mapState('artworks', ['artwork', 'elements']),
    isDisplayed() {
      const index = this.index;
      const current = this.current;

      return index === current || (!this.isCollectionFolded && this.element.id);
    },
    isCardinal() {
      const index = this.index;
      const current = this.current;
      const count = this.count;

      return index === 0 || index + 1 === current || index - 1 === current || index === count - 1;
    },
    isPreGap() {
      const index = this.index;
      const current = this.current;
      const count = this.count;

      return this.isCollectionFolded && index + 1 === current && index > 1;
    },
    isPostGap() {
      const index = this.index;
      const current = this.current;
      const count = this.count;

      return this.isCollectionFolded && index - 1 === current && index + 2 < count;
    },
    showLoadNext() {
      const index = this.index;
      const end = this.end;
      const count = this.count;

      return count > elementsPerPage && index === end - 1 && index !== count - 1;
    },
    showLoadPrev() {
      const index = this.index;
      const start = this.start;
      const count = this.count;

      return count > elementsPerPage && index === start && index !== 0;
    },
    isFirst() {
      return this.current === 0;
    },
    isLast() {
      return this.current === this.count - 1;
    },
    isActive() {
      const { isHeader } = this.$route.query;
      if (isHeader) {
        return false;
      } else {
        return this.isCurrent;
      }
    },
    isCurrent() {
      const slug = this.$route.params.id;
      const id = slug?.split('-').reverse()[0];

      const elementIndex = parseInt(this.$route.query.index);
      return (Number.isInteger(elementIndex) && elementIndex === this.index) || this.element._id === id;
    },
    isCollectionFolded() {
      return this.isFolded;
    },
    isSeparable() {
      return this.artwork.type === 'separable';
    },
    highlightedElement() {
      if (!this.artwork.highlights) return this.element;
      return applyHighlights(this.element, this.artwork.highlights);
    },
    printArtwork() {
      return this.isSeparable ? this.element : this.artwork;
    },
    titles() {
      return {
        share: this.$gettext('Partager'),
        create: this.$gettext('Créer une nouvelle notice rédactionnelle'),
      };
    },
  },
  methods: {
    ...mapMutations('artworks', ['assign']),
    setCurrent() {
      this.assign({ name: 'currentImage', data: 0 });
      if (this.isSeparable) {
        const elementId = this.elements[this.index]._id;
        const query = { ...this.$route.query, note: null };
        delete query.isHeader;
        this.$router.push({ name: 'artwork', params: { id: elementId, silent: false }, query });
      } else {
        const query = { ...this.$route.query, index: this.index, note: null };
        delete query.isHeader;
        this.$router.push({ name: 'artwork', query });
      }
    },
    loadPrev() {
      this.$emit('fetchPrevious');
    },
    loadNext() {
      this.$emit('fetchNext');
    },
    goTo(direction) {
      let index = this.current;
      direction === 'previous' ? index-- : index++;
      const elementId = this.elements[index]._id;
      if (this.isSeparable) {
        const query = Object.assign({}, this.$route.query);
        delete query.isHeader;
        this.$router.push({ name: 'artwork', params: { id: elementId, silent: false }, query });
      } else {
        const query = Object.assign({}, this.$route.query, { index });
        delete query.isHeader;
        this.$router.push({ name: 'artwork', query });
      }
    },
  },
};
</script>

<style lang="less">
.crew {
  padding: 0px 0 0 4px;
  &.gap {
    .sidebar-content {
      background: transparent;
      height: 30px;
      background-image: url(../../img/crew_bg.png);
      background-size: 100% 15px;

      &.after {
        background: transparent;
        height: 30px;
        background-image: url(../../img/crew_bg2.png);
        background-size: 100% 15px;
      }
    }
    .number {
      background-color: @compression-gap-number-background;
    }
  }
  &.folded {
    .sidebar-content {
      background: transparent;
      height: 30px;
      background-image: url(../../img/crew_bg.png);
      background-size: 100% 15px;

      > * {
        visibility: hidden;
        display: none;
      }

      &.after {
        background: transparent;
        height: 30px;
        background-image: url(../../img/crew_bg2.png);
        background-size: 100% 15px;
      }
    }

    .crew-next,
    .crew-prev {
      visibility: hidden;
      display: none;
    }
  }
  &.first {
    margin-top: 0;
    padding: 6px 0 0 4px;
  }

  .number {
    float: left;
    width: 22px;
    height: 22px;
    .border-radius(11px);
    background-color: @compression-number-background;
    text-align: center;
    line-height: 22px;
    cursor: pointer;
    cursor: hand;
  }

  .sidebar-content {
    margin-left: 30px;
    background-color: @compression-crew-content-background;
    position: relative;
  }

  .crew-prev,
  .crew-next {
    height: 20px;
    background-color: @compression-navigation-background;
    padding: 0;
    margin-left: 33px;
    a {
      display: block;
      height: 20px;
      width: 100%;

      i {
        color: @compression-navigation-icon-color;
        font-size: 54px;
        line-height: 20px;
      }
    }
  }
}
</style>
